import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { getEstadoChipClass } from '../../../utils/styles'; 

// Importamos las pestañas
import DetalleTabContrato from './Tabs/DetalleTabContrato';
import DatosContacto from '../../pages/DetalleCliente/Tabs/DatosContacto';
import ContratoCotizaciones from '../DetalleContrato/Tabs/ContratoCotizaciones';
import DistribucionTabContrato from './Tabs/DistribucionTabContrato'; 
import AumentosTab from './Tabs/AumentosTab';
import CobranzaARTTab from './Tabs/CobranzaARTTab';
// Importamos Modal de Edición de Cliente para la pestaña de Contacto
import ModalCliente from '../../pages/Clientes/ModalCliente';

import { useAuth } from '../../hooks/useAuth'; // ◄ Sincronización de rol nativa de Firestore

// Icono de Candado para Tabs Restringidos
const LockIcon = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
    </svg>
);

/**
 * DetalleContrato - Vista principal de gestión de un contrato ART individual.
 * Organiza la información en pestañas operativas y técnicas.
 * Blindado: La pestaña de Distribución solo es accesible para el Administrador.
 */
const DetalleContrato = () => {
    const { id: clienteId, contratoId } = useParams(); 
    const navigate = useNavigate();
    
    const [client, setClient] = useState(null);
    const [contrato, setContrato] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('detalle'); 
    
    // Estado para editar cliente desde la pestaña contacto
    const [showEditClientModal, setShowEditClientModal] = useState(false);

    // Consumimos el estado de rol y carga de sesión de forma reactiva en memoria
    const { isAdmin, loadingAuth } = useAuth(); // ◄ REEMPLAZA EL USO DE LOCALSTORAGE

    useEffect(() => {
        const fetchData = async () => {
            try {
                // 1. Cargar Cliente
                const clientRef = doc(db, 'public/data/clientes', clienteId);
                const clientSnap = await getDoc(clientRef);
                
                // 2. Cargar Contrato
                const contratoRef = doc(db, `public/data/clientes/${clienteId}/contratos`, contratoId);
                const contratoSnap = await getDoc(contratoRef);

                if (clientSnap.exists() && contratoSnap.exists()) {
                    setClient({ id: clientSnap.id, ...clientSnap.data() });
                    setContrato({ id: contratoSnap.id, ...contratoSnap.data() });
                } else {
                    console.error("No se encontró el cliente o el contrato");
                    navigate(`/clientes/${clienteId}`); 
                }
            } catch (error) {
                console.error("Error cargando datos:", error);
            } finally {
                setLoading(false);
            }
        };

        if (clienteId && contratoId) fetchData();
    }, [clienteId, contratoId, navigate]);

    // Actualizar contrato localmente tras edición
    const handleContratoUpdate = (msg) => {
        window.location.reload(); 
    };

    const handleClientUpdate = (msg, updatedClient) => {
        setClient(prev => ({ ...prev, ...updatedClient }));
        setShowEditClientModal(false);
    };

    // --- ESCUDO DE CARGA UNIFICADO (EVITA PARPADEOS) ---
    // Bloqueamos bajo el spinner elegante de carga hasta que se descarguen los datos del contrato
    // Y se haya verificado en memoria de forma reactiva si el usuario es Administrador.
    if (loading || loadingAuth) {
        return (
            <div className="flex justify-center items-center h-screen bg-gray-50 select-none">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    if (!client || !contrato) return null;

    return (
        <div className="min-h-screen bg-gray-50 pb-10 font-sans text-left">
        
            {/* HEADER DE CONTEXTO */}
            <header className="bg-white shadow-sm sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-20">
                        
                        {/* LADO IZQUIERDO: Volver a Cliente + Datos */}
                        <div className="flex items-center gap-4 text-left">
                            <button onClick={() => navigate(`/clientes/${clienteId}`)} className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-full hover:bg-gray-100" title="Volver al Cliente">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
                            </button>
                            <div className="text-left">
                                <h1 className="text-2xl font-bold text-gray-900 leading-tight flex items-center gap-3">
                                    Contrato #{contrato.nContrato}
                                    <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide border ${getEstadoChipClass(contrato.estadoPoliza)}`}>
                                        {contrato.estadoPoliza}
                                    </span>
                                </h1>
                                <p className="text-sm text-gray-500 flex items-center mt-1 text-left">
                                    <span className="font-medium text-gray-700 mr-1">Cliente:</span> {client.nombre} {client.apellido}
                                    <span className="mx-2 text-gray-300">|</span>
                                    <span className="font-medium text-indigo-600">{contrato.compania}</span>
                                </p>
                            </div>
                        </div>

                        {/* LADO DERECHO: Botones de Acción */}
                        <div className="flex items-center gap-2">
                            <button 
                                onClick={() => navigate('/BuscadorPolizasPage')}
                                className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-lg text-sm font-bold transition shadow-sm active:scale-95"
                            >
                                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
                                Volver al Buscador
                            </button>
                        </div>

                    </div>
                </div>
                
                {/* NAVEGACIÓN TABS */}
                <div className="border-t border-gray-200 bg-gray-50 select-none">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <nav className="-mb-px flex space-x-8 overflow-x-auto custom-scrollbar">
                            {[
                                { id: 'detalle', label: 'DETALLE' },
                                { id: 'contacto', label: 'CONTACTO' },
                                { id: 'cobranza', label: 'COBRANZA' },
                                { id: 'cotizaciones', label: 'COTIZACIONES' },
                                { id: 'aumentos', label: 'AUMENTOS' },
                                { id: 'distribucion', label: 'DISTRIBUCIÓN', restricted: true },
                            ].map((tab) => {
                                // Evaluamos la restricción cruzando contra la propiedad isAdmin de useAuth
                                const isLocked = tab.restricted && !isAdmin;

                                return (
                                    <button
                                        key={tab.id}
                                        onClick={() => !isLocked && setActiveTab(tab.id)}
                                        disabled={isLocked}
                                        className={`
                                            whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors uppercase flex items-center gap-2
                                            ${activeTab === tab.id 
                                                ? 'border-indigo-600 text-indigo-600' 
                                                : isLocked 
                                                    ? 'border-transparent text-gray-400 cursor-not-allowed'
                                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                                        `}
                                        title={isLocked ? "Acceso restringido a Administradores" : ""}
                                    >
                                        {tab.label}
                                        {isLocked && <LockIcon className="w-3 h-3 text-slate-400" />}
                                    </button>
                                );
                            })}
                        </nav>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-left">
                
                {activeTab === 'detalle' && (
                    <DetalleTabContrato 
                        contrato={contrato} 
                        client={client} 
                        onUpdate={handleContratoUpdate}
                    />
                )}

                {activeTab === 'contacto' && (
                    <DatosContacto 
                        client={client} 
                        onEditClick={() => setShowEditClientModal(true)} 
                    />
                )}

                {/* CORRECCIÓN CRÍTICA: Ahora pasamos "client" explícitamente a AumentosTab */}
                {activeTab === 'cobranza' && (
                    <CobranzaARTTab contrato={contrato} client={client} />
                )}

                {activeTab === 'cotizaciones' && (
                    <ContratoCotizaciones contrato={contrato} />
                )}

                {/* CORRECCIÓN CRÍTICA: Ahora pasamos "client" explícitamente a AumentosTab */}
                {activeTab === 'aumentos' && (
                    <AumentosTab contrato={contrato} client={client} />
                )}

                {/* Blindaje estricto de renderizado por rol para la pestaña Distribución */}
                {activeTab === 'distribucion' && isAdmin && (
                    <DistribucionTabContrato contrato={contrato} />
                )}

            </main>

            {/* Modal para editar Contacto del Cliente */}
            <ModalCliente
                show={showEditClientModal}
                client={client}
                onClose={() => setShowEditClientModal(false)}
                onSaveSuccess={handleClientUpdate}
            />

            <style>{`
                .custom-scrollbar::-webkit-scrollbar { height: 4px; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
            `}</style>
        </div>
    );
};

export default DetalleContrato;